/**
 * NewBuildModal - 80% width modal for creating new architecture version
 * 
 * Reuses existing components (DNASettings, EnvironmentSettings, IntegrationsSettings)
 * for consistent UI and proper checkbox/radio handling.
 * 
 * Tab flow:
 * 1. Project DNA - Target audience, tenancy, compliance, risk
 * 2. Environment - Cloud provider, region, compute, database
 * 3. Integrations - External APIs and services
 * 4. Special Instructions - Custom rules/constraints
 * 5. Summary - Overview of changes
 * 
 * Final step: Choose "Publish Constraints" (minor version) or "Build" (major version)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { 
  Loader2, Settings2, Server, Globe, FileCode, FileText,
  ArrowRight, ArrowLeft, CheckCircle, AlertTriangle,
  Hammer, X, Plus, ChevronDown, ChevronRight
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { deliveryConfigApi, infrastructureApi } from '../../services/api';

// Import existing components for consistent UI
import DNASettings from './DNASettings';
import EnvironmentSettings from './EnvironmentSettings';
import IntegrationsSettings from './IntegrationsSettings';

const TABS = [
  { id: 'dna', label: 'Project DNA', icon: Settings2 },
  { id: 'environment', label: 'Environment', icon: Server },
  { id: 'integrations', label: 'Integrations', icon: Globe },
  { id: 'instructions', label: 'Special Instructions', icon: FileCode },
  { id: 'summary', label: 'Summary', icon: FileText },
];

/**
 * NewBuildModal - Modal for creating or editing architecture versions/builds
 * 
 * Props:
 * - open: boolean - Whether modal is open
 * - onClose: function - Called when modal closes
 * - productId: string - Product ID
 * - isOwner: boolean - Whether user is owner
 * - currentVersion: number - Current published version
 * - isFirstBuild: boolean - Whether this is the first build
 * - editBuild: object|null - If provided, modal opens in edit mode for existing build
 *   - editBuild.task: the build task data
 *   - editBuild.status: 'not_started', 'in_progress', or 'complete'
 *   - editBuild.is_blocking: boolean
 *   - editBuild.config: draft config used to create the build
 */
const NewBuildModal = ({ 
  open, 
  onClose, 
  productId, 
  isOwner, 
  currentVersion, 
  isFirstBuild,
  editBuild = null  // When provided, we're editing an existing build
}) => {
  const [activeTab, setActiveTab] = useState('dna');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Config and options state
  const [config, setConfig] = useState(null);
  const [dnaOptions, setDnaOptions] = useState(null);
  const [envOptions, setEnvOptions] = useState(null);
  const [presets, setPresets] = useState(null);
  
  // Local edits (for tracking changes)
  const [dnaEdits, setDnaEdits] = useState({});
  const [envEdits, setEnvEdits] = useState({});
  const [customRules, setCustomRules] = useState([]);
  
  // Expanded sections for instructions
  const [expandedSections, setExpandedSections] = useState(['Instructions']);
  
  // Final step state
  const [showFinalStep, setShowFinalStep] = useState(false);
  const [buildTitle, setBuildTitle] = useState('');
  const [buildDescription, setBuildDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState('all');
  const [isBlocking, setIsBlocking] = useState(true);
  const [buildType, setBuildType] = useState('full');
  const [keyHolders, setKeyHolders] = useState([]);
  
  // Determine if we're editing an existing build
  const isEditMode = !!editBuild;

  // Load current config and options
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, dnaOptsRes, envOptsRes, presetsRes, keyHoldersRes] = await Promise.all([
        deliveryConfigApi.getConfig(productId),
        deliveryConfigApi.getDNAOptions(productId).catch(() => ({ data: { options: {} } })),
        deliveryConfigApi.getEnvironmentOptions(productId).catch(() => ({ data: { options: {} } })),
        deliveryConfigApi.getPresets(productId).catch(() => ({ data: { presets: {} } })),
        infrastructureApi.getMcpKeyHolders(productId).catch(() => ({ data: { key_holders: [] } }))
      ]);
      
      const loadedConfig = configRes.data?.delivery_config || configRes.data;
      setConfig(loadedConfig);
      setDnaOptions(dnaOptsRes.data?.options || dnaOptsRes.data);
      setEnvOptions(envOptsRes.data?.options || envOptsRes.data);
      setPresets(presetsRes.data?.presets || presetsRes.data);
      setKeyHolders(keyHoldersRes.data?.key_holders || []);
      
      // Initialize local edits from current config (or draft config if editing)
      if (isEditMode && editBuild.draft_config) {
        // Use the draft config from when the build was created
        setDnaEdits(editBuild.draft_config?.project_dna || loadedConfig?.project_dna || {});
        setEnvEdits(editBuild.draft_config?.environment || loadedConfig?.environment || {});
        setCustomRules(editBuild.draft_config?.constraint_templates?.custom_rules || loadedConfig?.constraint_templates?.custom_rules || []);
        
        // Pre-populate build fields from existing task
        setBuildTitle(editBuild.task?.title || '');
        setBuildDescription(editBuild.task?.description || '');
        setAssigneeId(editBuild.task?.assignee_id || 'all');
        setIsBlocking(editBuild.is_blocking !== false);
      } else {
        setDnaEdits(loadedConfig?.project_dna || {});
        setEnvEdits(loadedConfig?.environment || {});
        setCustomRules(loadedConfig?.constraint_templates?.custom_rules || []);
        
        // Set default build title
        setBuildTitle(isFirstBuild ? 'Bootstrap Infrastructure' : 'Architecture Update');
      }
      
    } catch (error) {
      console.error('Failed to load config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [productId, isFirstBuild, isEditMode, editBuild]);

  useEffect(() => {
    if (open && productId) {
      loadConfig();
      setActiveTab('dna');
      setShowFinalStep(false);
    }
  }, [open, productId, loadConfig]);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Navigate tabs
  const currentTabIndex = TABS.findIndex(t => t.id === activeTab);
  
  const handleNext = () => {
    if (currentTabIndex < TABS.length - 1) {
      setActiveTab(TABS[currentTabIndex + 1].id);
    }
  };
  
  const handlePrev = () => {
    if (currentTabIndex > 0) {
      setActiveTab(TABS[currentTabIndex - 1].id);
    }
  };

  // Handle DNA update (local state, saved on publish/build)
  const handleDNAUpdate = (updates) => {
    setDnaEdits(prev => ({ ...prev, ...updates }));
  };

  // Handle Environment update (local state, saved on publish/build)
  const handleEnvironmentUpdate = (updates) => {
    // Remove internal flags
    const cleanUpdates = { ...updates };
    delete cleanUpdates._is_infrastructure_change;
    
    setEnvEdits(prev => {
      // Handle nested updates (e.g., networking.urls)
      const newState = { ...prev };
      Object.entries(cleanUpdates).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          newState[key] = { ...(newState[key] || {}), ...value };
        } else {
          newState[key] = value;
        }
      });
      return newState;
    });
  };

  // Handle proceed to final step
  const handleProceedToFinal = () => {
    setShowFinalStep(true);
  };

  // Handle publish constraints (minor version)
  const handlePublish = async () => {
    try {
      setSaving(true);
      
      // Save config changes
      await deliveryConfigApi.updateDNA(productId, dnaEdits);
      await deliveryConfigApi.updateEnvironment(productId, envEdits);
      
      // Publish as new minor version
      const response = await infrastructureApi.publishConstraints(productId, {
        custom_rules: customRules
      });
      
      const newVersion = response.data?.version || `${currentVersion || 1}.1`;
      toast.success(`Published version ${newVersion}`);
      
      onClose?.({ success: true, type: 'publish', version: newVersion });
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish constraints');
    } finally {
      setSaving(false);
    }
  };

  // Handle create build (major version)
  const handleCreateBuild = async () => {
    if (!buildTitle.trim()) {
      toast.error('Please enter a title for the build');
      return;
    }
    
    try {
      setSaving(true);
      
      // Save config changes first
      await deliveryConfigApi.updateDNA(productId, dnaEdits);
      await deliveryConfigApi.updateEnvironment(productId, envEdits);
      
      // Build the draft config snapshot to store with the build
      // This is what Claude will see via MCP
      const draftConfig = {
        project_dna: dnaEdits,
        environment: envEdits,
        constraint_templates: {
          custom_rules: customRules
        }
      };
      
      // Create build with draft config
      await infrastructureApi.createArchitectureFeature(productId, {
        title: buildTitle.trim(),
        description: buildDescription.trim(),
        assignee_id: assigneeId,
        is_blocking: isBlocking,
        build_type: buildType,
        draft_config: draftConfig
      });
      
      toast.success('Build created successfully');
      onClose?.({ success: true, type: 'build' });
    } catch (error) {
      console.error('Failed to create build:', error);
      toast.error(error.response?.data?.detail || 'Failed to create build');
    } finally {
      setSaving(false);
    }
  };

  // Handle update existing build (edit mode)
  const handleUpdateBuild = async () => {
    if (!buildTitle.trim()) {
      toast.error('Please enter a title for the build');
      return;
    }
    
    try {
      setSaving(true);
      
      // Save config changes first
      await deliveryConfigApi.updateDNA(productId, dnaEdits);
      await deliveryConfigApi.updateEnvironment(productId, envEdits);
      
      // Build the draft config snapshot
      const draftConfig = {
        project_dna: dnaEdits,
        environment: envEdits,
        constraint_templates: {
          custom_rules: customRules
        }
      };
      
      // Update build with full data including draft config
      await infrastructureApi.updateBuild(productId, {
        title: buildTitle.trim(),
        description: buildDescription.trim(),
        assignee_id: assigneeId,
        is_blocking: isBlocking,
        draft_config: draftConfig
      });
      
      toast.success('Build updated');
      onClose?.({ success: true, type: 'update' });
    } catch (error) {
      console.error('Failed to update build:', error);
      toast.error(error.response?.data?.detail || 'Failed to update build');
    } finally {
      setSaving(false);
    }
  };

  // Handle publish and cancel build
  const handlePublishAndCancel = async () => {
    try {
      setSaving(true);
      
      // Save config changes
      await deliveryConfigApi.updateDNA(productId, dnaEdits);
      await deliveryConfigApi.updateEnvironment(productId, envEdits);
      
      // Publish as new minor version
      await infrastructureApi.publishConstraints(productId, {
        custom_rules: customRules
      });
      
      // Cancel the build
      await infrastructureApi.cancelBuild(productId);
      
      toast.success('Constraints published and build cancelled');
      onClose?.({ success: true, type: 'publish_and_cancel' });
    } catch (error) {
      console.error('Failed to publish and cancel:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish and cancel build');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel build only
  const handleCancelBuild = async () => {
    try {
      setSaving(true);
      const response = await infrastructureApi.cancelBuild(productId);
      
      if (response.data?.return_to_onboarding) {
        toast.success('Build cancelled - returning to setup');
        onClose?.({ success: true, type: 'cancel', returnToOnboarding: true });
      } else {
        toast.success('Build cancelled');
        onClose?.({ success: true, type: 'cancel' });
      }
    } catch (error) {
      console.error('Failed to cancel build:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel build');
    } finally {
      setSaving(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (showFinalStep) {
      setShowFinalStep(false);
    } else {
      onClose?.({ success: false });
    }
  };

  // Format value for display in summary
  const formatValue = (value) => {
    if (Array.isArray(value)) {
      return value.map(v => v.replace(/_/g, ' ')).join(', ');
    }
    if (typeof value === 'string') {
      return value.replace(/_/g, ' ');
    }
    return String(value);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={() => handleClose()}>
      <DialogContent 
        className="max-w-[85vw] h-[85vh] flex flex-col p-0"
        data-testid="new-build-modal"
      >
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">
                {isEditMode 
                  ? (showFinalStep ? 'Update Build' : 'Edit Build')
                  : (showFinalStep ? 'Publish or Build' : (isFirstBuild ? 'Define Architecture' : 'New Version'))
                }
              </DialogTitle>
              <DialogDescription>
                {isEditMode
                  ? (showFinalStep ? 'Choose how to apply your changes' : 'Modify configuration and settings for this build')
                  : (showFinalStep 
                    ? 'Choose how to apply your changes'
                    : 'Configure your technical stack and constraints'
                  )
                }
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : showFinalStep ? (
            /* Final Step - Different for edit mode vs create mode */
            <div className="h-full overflow-y-auto p-6">
              <div className="max-w-2xl mx-auto space-y-6">
                {isEditMode ? (
                  /* Edit Mode Final Step */
                  <>
                    <Alert className="bg-blue-50 border-blue-200">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        Update the build settings below. You can also publish constraints or cancel the build entirely.
                      </AlertDescription>
                    </Alert>

                    {/* Build Settings */}
                    <Card className="border-2">
                      <CardHeader className="pb-3">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-orange-100 rounded-lg">
                            <Hammer className="w-6 h-6 text-orange-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">Build Settings</h3>
                            <p className="text-sm text-gray-600">
                              Modify the build task details
                            </p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4 pt-0">
                        <div className="space-y-3 pl-16">
                          <div className="space-y-1">
                            <Label htmlFor="edit-build-title">Title</Label>
                            <Input
                              id="edit-build-title"
                              value={buildTitle}
                              onChange={(e) => setBuildTitle(e.target.value)}
                              placeholder="e.g., Bootstrap AWS Environment"
                              data-testid="edit-build-title-input"
                            />
                          </div>
                          
                          <div className="space-y-1">
                            <Label htmlFor="edit-build-description">Description</Label>
                            <Textarea
                              id="edit-build-description"
                              value={buildDescription}
                              onChange={(e) => setBuildDescription(e.target.value)}
                              placeholder="Describe what infrastructure changes are needed..."
                              rows={2}
                              data-testid="edit-build-description-input"
                            />
                          </div>
                          
                          <div className="space-y-1">
                            <Label htmlFor="edit-build-assignee">Assign to</Label>
                            <Select value={assigneeId} onValueChange={setAssigneeId}>
                              <SelectTrigger id="edit-build-assignee" data-testid="edit-build-assignee-select">
                                <SelectValue placeholder="Select assignee" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Developers</SelectItem>
                                {keyHolders.map((holder) => (
                                  <SelectItem key={holder.user_id} value={holder.user_id}>
                                    {holder.name || holder.email}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="space-y-0.5">
                              <Label htmlFor="edit-build-blocking" className="cursor-pointer">
                                Blocking Build
                              </Label>
                              <p className="text-xs text-gray-500">
                                {isBlocking 
                                  ? 'Other features blocked until complete'
                                  : 'Other features can be worked in parallel'
                                }
                              </p>
                            </div>
                            <Switch
                              id="edit-build-blocking"
                              checked={isBlocking}
                              onCheckedChange={setIsBlocking}
                              data-testid="edit-build-blocking-toggle"
                            />
                          </div>
                          
                          <Button
                            onClick={handleUpdateBuild}
                            disabled={saving || !buildTitle.trim()}
                            className="w-full"
                            data-testid="update-build-btn"
                          >
                            {saving ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Hammer className="w-4 h-4 mr-2" />
                            )}
                            Update Build
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Publish and Cancel Option */}
                    <Card 
                      className="cursor-pointer hover:border-purple-300 hover:bg-purple-50/50 transition-all"
                      onClick={handlePublishAndCancel}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-purple-100 rounded-lg">
                            <FileText className="w-6 h-6 text-purple-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">Publish and Cancel Build</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              Save constraints as a new minor version and cancel the build task. 
                              No infrastructure changes will be made.
                            </p>
                            <Badge variant="outline">Minor Version Update</Badge>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Cancel Build Option */}
                    <Card 
                      className="cursor-pointer hover:border-red-300 hover:bg-red-50/50 transition-all border-red-200"
                      onClick={handleCancelBuild}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-red-100 rounded-lg">
                            <X className="w-6 h-6 text-red-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">Cancel Build</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              Cancel this build without saving changes.
                              {currentVersion === 0 && (
                                <span className="text-red-600 font-medium"> This will return you to the setup screen.</span>
                              )}
                            </p>
                            <Badge variant="destructive">Discard Build</Badge>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  /* Create Mode Final Step */
                  <>
                    <Alert className="bg-blue-50 border-blue-200">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        Your architecture configuration has been updated. Choose how to apply these changes.
                      </AlertDescription>
                    </Alert>

                    {/* Publish Option */}
                    <Card 
                      className="cursor-pointer hover:border-purple-300 hover:bg-purple-50/50 transition-all"
                      onClick={handlePublish}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-purple-100 rounded-lg">
                            <FileText className="w-6 h-6 text-purple-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">Publish Constraints</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              Save as a new minor version. No infrastructure changes - just update the constraints 
                              that guide your AI assistant.
                            </p>
                            <Badge variant="outline">Minor Version Update</Badge>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Build Option */}
                    <Card className="border-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-orange-100 rounded-lg">
                        <Hammer className="w-6 h-6 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">Create Build</h3>
                        <p className="text-sm text-gray-600">
                          Create a build request for your AI assistant to provision/update infrastructure.
                          This will create a new major version.
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-0">
                    <div className="space-y-3 pl-16">
                      {/* Build Type Selection */}
                      <div className="space-y-2">
                        <Label>Build Type</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <div 
                            className={cn(
                              "p-3 rounded-lg border-2 cursor-pointer transition-all",
                              buildType === 'full' 
                                ? "border-orange-400 bg-orange-50" 
                                : "border-gray-200 hover:border-gray-300"
                            )}
                            onClick={() => setBuildType('full')}
                            data-testid="build-type-full"
                          >
                            <div className="font-medium text-sm mb-1">Full Build</div>
                            <p className="text-xs text-gray-500">
                              Build complete infrastructure based on current configuration
                            </p>
                          </div>
                          <div 
                            className={cn(
                              "p-3 rounded-lg border-2 cursor-pointer transition-all",
                              buildType === 'diff' 
                                ? "border-orange-400 bg-orange-50" 
                                : "border-gray-200 hover:border-gray-300",
                              currentVersion === 0 && "opacity-50 cursor-not-allowed"
                            )}
                            onClick={() => currentVersion > 0 && setBuildType('diff')}
                            data-testid="build-type-diff"
                          >
                            <div className="font-medium text-sm mb-1">Diff Build</div>
                            <p className="text-xs text-gray-500">
                              {currentVersion > 0 
                                ? `Only changes since v${currentVersion}` 
                                : "Not available for first build"
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="build-title">Title</Label>
                        <Input
                          id="build-title"
                          value={buildTitle}
                          onChange={(e) => setBuildTitle(e.target.value)}
                          placeholder="e.g., Bootstrap AWS Environment"
                          data-testid="build-title-input"
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <Label htmlFor="build-description">Description</Label>
                        <Textarea
                          id="build-description"
                          value={buildDescription}
                          onChange={(e) => setBuildDescription(e.target.value)}
                          placeholder="Describe what infrastructure changes are needed..."
                          rows={2}
                          data-testid="build-description-input"
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <Label htmlFor="build-assignee">Assign to</Label>
                        <Select value={assigneeId} onValueChange={setAssigneeId}>
                          <SelectTrigger id="build-assignee" data-testid="build-assignee-select">
                            <SelectValue placeholder="Select assignee" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Developers</SelectItem>
                            {keyHolders.map((holder) => (
                              <SelectItem key={holder.user_id} value={holder.user_id}>
                                {holder.name || holder.email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="space-y-0.5">
                          <Label htmlFor="build-blocking" className="cursor-pointer">
                            Blocking Build
                          </Label>
                          <p className="text-xs text-gray-500">
                            {isBlocking 
                              ? 'Other features blocked until complete'
                              : 'Other features can be worked in parallel'
                            }
                          </p>
                        </div>
                        <Switch
                          id="build-blocking"
                          checked={isBlocking}
                          onCheckedChange={setIsBlocking}
                          data-testid="build-blocking-toggle"
                        />
                      </div>
                      
                      <Button
                        onClick={handleCreateBuild}
                        disabled={saving || !buildTitle.trim()}
                        className="w-full"
                        data-testid="create-build-btn"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Hammer className="w-4 h-4 mr-2" />
                        )}
                        Create Build
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                  </>
                )}
              </div>
            </div>
          ) : (
            /* Tab Content */
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
              <TabsList className="mx-6 mt-4 grid grid-cols-5 shrink-0">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <TabsTrigger key={tab.id} value={tab.id} className="gap-2">
                      <Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{tab.label}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              <div className="flex-1 overflow-y-auto p-6">
                {/* Project DNA Tab - Uses DNASettings component */}
                <TabsContent value="dna" className="mt-0">
                  <DNASettings
                    config={dnaEdits}
                    options={dnaOptions}
                    isOwner={isOwner}
                    saving={saving}
                    onUpdate={handleDNAUpdate}
                  />
                </TabsContent>

                {/* Environment Tab - Uses EnvironmentSettings component */}
                <TabsContent value="environment" className="mt-0">
                  <EnvironmentSettings
                    productId={productId}
                    config={envEdits}
                    options={envOptions}
                    isOwner={isOwner}
                    saving={saving}
                    onUpdate={handleEnvironmentUpdate}
                  />
                </TabsContent>

                {/* Integrations Tab - Uses IntegrationsSettings component */}
                <TabsContent value="integrations" className="mt-0">
                  <IntegrationsSettings
                    productId={productId}
                    isOwner={isOwner}
                  />
                </TabsContent>

                {/* Special Instructions Tab */}
                <TabsContent value="instructions" className="mt-0 space-y-4">
                  <Card>
                    <Collapsible open={expandedSections.includes('Instructions')} onOpenChange={() => toggleSection('Instructions')}>
                      <CollapsibleTrigger asChild>
                        <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors py-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium">Custom Rules</CardTitle>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{customRules.length} rules</Badge>
                              {expandedSections.includes('Instructions') ? (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </CardHeader>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <CardContent className="pt-0 space-y-4">
                          <p className="text-sm text-gray-600">
                            Add custom rules and constraints that your AI assistant should follow when implementing features.
                          </p>
                          
                          {/* Existing Rules */}
                          {customRules.length > 0 && (
                            <div className="space-y-2">
                              {customRules.map((rule, index) => (
                                <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                                  <span className="flex-1 text-sm text-gray-700">
                                    {typeof rule === 'string' ? rule : rule.rule}
                                  </span>
                                  {isOwner && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setCustomRules(prev => prev.filter((_, i) => i !== index))}
                                      className="shrink-0 text-red-500 hover:text-red-600"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Add New Rule */}
                          {isOwner && (
                            <NewRuleInput onAdd={(rule) => setCustomRules(prev => [...prev, { rule, type: 'custom' }])} />
                          )}
                        </CardContent>
                      </CollapsibleContent>
                    </Collapsible>
                  </Card>
                </TabsContent>

                {/* Summary Tab */}
                <TabsContent value="summary" className="mt-0 space-y-4">
                  <Alert className="bg-blue-50 border-blue-200">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      Review your architecture configuration before publishing or creating a build.
                    </AlertDescription>
                  </Alert>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Project DNA Summary */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Settings2 className="w-4 h-4 text-purple-500" />
                          Project DNA
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                          <div>
                            <span className="text-gray-500">Tenancy:</span>{' '}
                            <span className="font-medium">{formatValue(dnaEdits.tenancy_mode) || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Compliance:</span>{' '}
                            <span className="font-medium">
                              {dnaEdits.compliance?.length > 0 ? formatValue(dnaEdits.compliance) : 'None'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Delivery:</span>{' '}
                            <span className="font-medium">{formatValue(dnaEdits.delivery_lifecycle) || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Security:</span>{' '}
                            <span className="font-medium">{formatValue(dnaEdits.security_model) || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Architecture:</span>{' '}
                            <span className="font-medium">{formatValue(dnaEdits.architecture_style) || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Quality:</span>{' '}
                            <span className="font-medium">{formatValue(dnaEdits.quality_tier) || 'Not set'}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Core Infrastructure Summary */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Server className="w-4 h-4 text-blue-500" />
                          Core Infrastructure
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                          <div>
                            <span className="text-gray-500">Cloud:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.cloud_provider) || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Region:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.region) || 'Not set'}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-gray-500">Compute:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.compute) || 'Not set'}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Services Summary */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Globe className="w-4 h-4 text-green-500" />
                          Services
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                          <div>
                            <span className="text-gray-500">Database:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.services?.database) || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Storage:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.services?.storage) || 'None'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Cache:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.services?.cache) || 'None'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Queue:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.services?.queue) || 'None'}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-gray-500">Auth:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.services?.auth) || 'Not set'}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Networking Summary */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Server className="w-4 h-4 text-cyan-500" />
                          Networking
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                          <div>
                            <span className="text-gray-500">Ingress:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.networking?.ingress) || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">API Structure:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.networking?.api_structure) || 'Not set'}</span>
                          </div>
                        </div>
                        {(envEdits.networking?.urls?.production || envEdits.networking?.urls?.staging) && (
                          <div className="mt-2 pt-2 border-t">
                            <span className="text-gray-500 text-xs">URLs:</span>
                            <div className="grid grid-cols-2 gap-1 mt-1">
                              {envEdits.networking?.urls?.production && (
                                <div className="text-xs truncate">
                                  <span className="text-gray-400">prod:</span> {envEdits.networking.urls.production}
                                </div>
                              )}
                              {envEdits.networking?.urls?.staging && (
                                <div className="text-xs truncate">
                                  <span className="text-gray-400">stage:</span> {envEdits.networking.urls.staging}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Workflow Summary */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <FileCode className="w-4 h-4 text-indigo-500" />
                          Workflow
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                          <div>
                            <span className="text-gray-500">Branching:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.workflow?.branching) || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">PR Approvals:</span>{' '}
                            <span className="font-medium">{envEdits.workflow?.pr_approvals ?? 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Repo Host:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.workflow?.repo_host) || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">CI/CD:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.workflow?.ci_provider) || 'Not set'}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Secrets Summary */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <FileCode className="w-4 h-4 text-red-500" />
                          Secrets & Config
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                          <div>
                            <span className="text-gray-500">Secret Store:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.secrets?.store) || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Naming:</span>{' '}
                            <span className="font-medium">{formatValue(envEdits.secrets?.naming) || 'Not set'}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Special Instructions Summary */}
                  {customRules.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <FileCode className="w-4 h-4 text-orange-500" />
                          Special Instructions ({customRules.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="text-sm space-y-1">
                          {customRules.map((rule, index) => (
                            <li key={index} className="text-gray-700">
                              • {typeof rule === 'string' ? rule : rule.rule}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          )}
        </div>

        {/* Footer */}
        {!loading && !showFinalStep && (
          <div className="px-6 py-4 border-t flex items-center justify-between shrink-0">
            <Button
              variant="outline"
              onClick={currentTabIndex === 0 ? handleClose : handlePrev}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {currentTabIndex === 0 ? 'Cancel' : 'Previous'}
            </Button>
            
            <div className="flex items-center gap-2">
              {/* Tab indicators */}
              {TABS.map((tab, index) => (
                <div
                  key={tab.id}
                  className={cn(
                    "w-2 h-2 rounded-full transition-colors",
                    index === currentTabIndex ? "bg-purple-500" : "bg-gray-200"
                  )}
                />
              ))}
            </div>
            
            {currentTabIndex === TABS.length - 1 ? (
              <Button onClick={handleProceedToFinal}>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleNext}>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        )}
        
        {!loading && showFinalStep && (
          <div className="px-6 py-4 border-t flex items-center justify-between shrink-0">
            <Button variant="outline" onClick={() => setShowFinalStep(false)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Edit
            </Button>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// New Rule Input Component
const NewRuleInput = ({ onAdd }) => {
  const [newRule, setNewRule] = useState('');

  const handleAdd = () => {
    if (newRule.trim()) {
      onAdd(newRule.trim());
      setNewRule('');
    }
  };

  return (
    <div className="flex gap-2">
      <Textarea
        value={newRule}
        onChange={(e) => setNewRule(e.target.value)}
        placeholder="e.g., All database queries MUST filter by org_id for multi-tenant isolation"
        rows={2}
        className="flex-1"
      />
      <Button onClick={handleAdd} disabled={!newRule.trim()}>
        <Plus className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default NewBuildModal;
